import React, { useRef, useEffect } from 'react';
import './Projects.css';
import ScrollSection from '../../components/ScrollSection';
import { useContent } from '../../context/ContentContext';

function ProjectCard({ project }) {
  return (
    <div className="project-card">
      <div
        className="project-card-image"
        style={{ background: project.gradient }}
      >
        <i className={"fas fa-" + project.icon + " project-card-icon"}></i>
        <span className="project-card-image-label">{project.title}</span>
        {project.status === 'active' && (
          <span className="project-status-badge">Active</span>
        )}
      </div>
      <div className="project-card-body">
        <p className="project-card-desc">{project.description}</p>
        <div className="project-card-tech">
          {project.technologies.map(tech => (
            <span key={tech} className="project-tech-pill">{tech}</span>
          ))}
        </div>
        <button
          className="btn btn-primary project-card-btn"
          onClick={() => window.open(project.url, '_blank', 'noopener,noreferrer')}
        >
          View Project
        </button>
      </div>
    </div>
  );
}

function Projects() {
  const { content, loading } = useContent();

  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const velocityRef = useRef(0);
  const lastXRef = useRef(0);
  const momentumRef = useRef(null);
  const hasMoved = useRef(false);

  const projects = content ? content.projects : [];
  const allCards = [...projects, ...projects, ...projects];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const third = el.scrollWidth / 3;
      if (el.scrollLeft >= third * 2) el.scrollLeft -= third;
      else if (el.scrollLeft <= 0) el.scrollLeft += third;
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    setTimeout(() => { if (el) el.scrollLeft = el.scrollWidth / 3; }, 50);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [projects]);

  const handlePointerDown = (e) => {
    isDragging.current = true;
    hasMoved.current = false;
    dragStartX.current = e.pageX - scrollRef.current.offsetLeft;
    dragScrollLeft.current = scrollRef.current.scrollLeft;
    lastXRef.current = e.pageX;
    velocityRef.current = 0;
    scrollRef.current.style.cursor = 'grabbing';
    if (momentumRef.current) cancelAnimationFrame(momentumRef.current);
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5;
    if (Math.abs(walk) > 5) hasMoved.current = true;
    velocityRef.current = e.pageX - lastXRef.current;
    lastXRef.current = e.pageX;
    scrollRef.current.scrollLeft = dragScrollLeft.current - walk;
  };

  const handlePointerUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    scrollRef.current.style.cursor = 'grab';
    if (!hasMoved.current) return;
    let velocity = velocityRef.current * 1.5;
    const applyMomentum = () => {
      if (Math.abs(velocity) < 0.5) return;
      scrollRef.current.scrollLeft -= velocity;
      velocity *= 0.92;
      momentumRef.current = requestAnimationFrame(applyMomentum);
    };
    momentumRef.current = requestAnimationFrame(applyMomentum);
  };

  if (loading || !content) return null;

  return (
    <ScrollSection name="projects">
      <div className="section-container">
        <h2 className="section-title" data-aos="fade-up" data-aos-duration="800">Projects</h2>
        <p className="projects-subtitle" data-aos="fade-up" data-aos-duration="800" data-aos-delay="100">
          Production systems, home lab infrastructure, and shipped applications.
        </p>
        <div
          className="project-carousel"
          ref={scrollRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="200"
        >
          {allCards.map((project, i) => (
            <ProjectCard key={project.title + String(i)} project={project} />
          ))}
        </div>
      </div>
    </ScrollSection>
  );
}

export default Projects;
